export const rotaStatusLabel = {
  rascunho: 'Rascunho',
  em_andamento: 'Em andamento',
  finalizada: 'Finalizada',
};

export const rotaStatusColor = {
  rascunho: 'bg-amber-100 text-amber-800',
  em_andamento: 'bg-emerald-100 text-emerald-800',
  finalizada: 'bg-slate-100 text-slate-700',
};

export const toCurrency = (value) => `R$ ${Number(value || 0).toFixed(2)}`;

export const safeNumber = (value) => Number(value || 0);

export const getStandAloneTotal = (rota) =>
  (rota?.visitas || []).reduce((acc, visita) => acc + safeNumber(visita.custo_real), 0);

export const getMetodo2Value = (visita) => {
  if (visita?.custo_trecho != null) {
    return { valor: safeNumber(visita.custo_trecho), estimado: false };
  }
  return { valor: safeNumber(visita?.custo_proporcional), estimado: true };
};

export const getEconomiaRota = (rota) => {
  const standAlone = getStandAloneTotal(rota);
  const custoRota = safeNumber(rota?.custo_total);
  return Math.max(0, standAlone - custoRota);
};

export const normalizePecaValorTotal = (peca) => {
  if (peca?.valor_total != null) return safeNumber(peca.valor_total);
  return safeNumber(peca?.quantidade) * safeNumber(peca?.custo_unitario);
};

export const buildManutencaoIndex = (manutencoes, validRotaIds) => {
  const index = new Map();
  const rotaIdSet = new Set(validRotaIds);

  (manutencoes || []).forEach((manutencao) => {
    if (!manutencao?.rota_id || !rotaIdSet.has(manutencao.rota_id) || !manutencao?.cliente_id) return;
    const key = `${manutencao.rota_id}::${manutencao.cliente_id}`;
    const current = index.get(key) || {
      manutencoes: 0,
      custo_pecas_total: 0,
      pecas: [],
    };

    const pecas = manutencao.pecas || [];
    const pecaEntries = pecas.map((peca) => ({
      nome: peca.nome || peca.codigo || 'Peça sem nome',
      quantidade: safeNumber(peca.quantidade),
      valor_total: normalizePecaValorTotal(peca),
    }));

    current.manutencoes += 1;
    current.custo_pecas_total += safeNumber(manutencao.custo_pecas);
    current.pecas.push(...pecaEntries);
    index.set(key, current);
  });

  return index;
};

export const buildResumoPorCliente = (rotas, manutencaoIndex) => {
  const resumo = new Map();

  (rotas || []).forEach((rota) => {
    (rota.visitas || []).forEach((visita) => {
      if (!visita?.cliente_id) return;
      const clienteId = visita.cliente_id;
      const key = `${rota.id}::${clienteId}`;
      const material = manutencaoIndex?.get(key);
      const metodo2 = getMetodo2Value(visita);
      const standAlone = safeNumber(visita.custo_real);

      const current = resumo.get(clienteId) || {
        cliente_id: clienteId,
        cliente_codigo: visita.cliente_codigo || '',
        cliente_nome: visita.cliente_nome || clienteId,
        visitas: 0,
        metodo2_total: 0,
        standalone_total: 0,
        economia_total: 0,
        materiais_total: 0,
        metodo2_estimado_count: 0,
      };

      current.visitas += 1;
      current.metodo2_total += metodo2.valor;
      current.standalone_total += standAlone;
      current.materiais_total += safeNumber(material?.custo_pecas_total);
      current.metodo2_estimado_count += metodo2.estimado ? 1 : 0;
      resumo.set(clienteId, current);
    });
  });

  return Array.from(resumo.values())
    .map((item) => {
      const economia = Math.max(0, item.standalone_total - item.metodo2_total);
      return {
        ...item,
        economia_total: economia,
        metodo2_medio: item.visitas > 0 ? item.metodo2_total / item.visitas : 0,
        standalone_medio: item.visitas > 0 ? item.standalone_total / item.visitas : 0,
        metodo2_100_estimado: item.visitas > 0 && item.metodo2_estimado_count === item.visitas,
      };
    })
    .sort((a, b) => b.metodo2_total - a.metodo2_total);
};

export const buildSimulacaoRota7Clientes = () => {
  const custoPorKm = 1.2;
  // base->c1 ... c9->base
  const kmTrechos = [315, 6, 12, 18, 9, 14, 11, 308, 34, 2];
  // Distância direta (apenas ida) da base para cada cliente.
  // Stand-alone sempre considera ida e volta da base para o mesmo cliente.
  const kmBaseCliente = [315, 320, 326, 309, 302, 306, 320, 30, 2];
  const nomes = [
    'Cliente Ribeirao 1',
    'Cliente Ribeirao 2',
    'Cliente Serrana',
    'Cliente Cravinhos',
    'Cliente Brodowski',
    'Cliente Jardinopolis',
    'Cliente Batatais',
    'Cliente Paulinia',
    'Cliente Campinas (2km base)',
  ];

  const kmAtribuido = [
    kmTrechos[0] + kmTrechos[1] / 2,
    kmTrechos[1] / 2 + kmTrechos[2] / 2,
    kmTrechos[2] / 2 + kmTrechos[3] / 2,
    kmTrechos[3] / 2 + kmTrechos[4] / 2,
    kmTrechos[4] / 2 + kmTrechos[5] / 2,
    kmTrechos[5] / 2 + kmTrechos[6] / 2,
    kmTrechos[6] / 2 + kmTrechos[7] / 2,
    kmTrechos[7] / 2 + kmTrechos[8] / 2,
    kmTrechos[8] / 2 + kmTrechos[9],
  ];
  const kmTotal = kmTrechos.reduce((acc, km) => acc + km, 0);
  const custoTotal = kmTotal * custoPorKm;
  const totalKmBase = kmBaseCliente.reduce((acc, km) => acc + km, 0);

  const visitas = nomes.map((nome, index) => {
    const custoTrecho = kmAtribuido[index] * custoPorKm;
    const custoStandAlone = kmBaseCliente[index] * 2 * custoPorKm;
    const custoProporcional = (kmBaseCliente[index] / totalKmBase) * custoTotal;
    return {
      id: `sim-visita-${index + 1}`,
      cliente_id: `sim-cliente-${index + 1}`,
      cliente_codigo: `SIM${String(index + 1).padStart(3, '0')}`,
      cliente_nome: nome,
      ordem: index + 1,
      custo_real: custoStandAlone,
      custo_proporcional: custoProporcional,
      custo_trecho: custoTrecho,
      km_trecho_atribuido: kmAtribuido[index],
      km_base_cliente: kmBaseCliente[index],
    };
  });

  return {
    id: 'sim-rota-7-clientes',
    data: new Date().toISOString().split('T')[0],
    tecnico_nome: 'Tecnico Simulacao',
    carro_placa: 'SIM-7001',
    status: 'finalizada',
    km_total: kmTotal,
    custo_total: custoTotal,
    visitas,
    is_simulacao: true,
  };
};

/**
 * Várias rotas fictícias + manutenções para demonstrar o relatório consolidado por cliente
 * (mesmo cliente em rotas diferentes → várias visitas agregadas; materiais por rota+cliente).
 */
export const buildSimulacaoConsolidadoExemplo = () => {
  const mkVisita = (id, ordem, clienteId, codigo, nome, custoTrecho, custoStandAlone, custoProporcionalFallback = 0) => ({
    id,
    cliente_id: clienteId,
    cliente_codigo: codigo,
    cliente_nome: nome,
    ordem,
    custo_real: custoStandAlone,
    custo_proporcional: custoProporcionalFallback,
    custo_trecho: custoTrecho,
  });

  const rotaA = {
    id: 'sim-cons-rota-a',
    data: '2026-03-18',
    tecnico_nome: 'Técnico Exemplo',
    carro_placa: 'CONS-1001',
    status: 'finalizada',
    km_total: 210,
    custo_total: 252,
    is_simulacao: true,
    visitas: [
      mkVisita('sim-cons-v-a1', 1, 'ex-cli-ribeirao', 'EX-RP01', 'Indústria Ribeirão', 95, 768, 0),
      mkVisita('sim-cons-v-a2', 2, 'ex-cli-paulinia', 'EX-PL01', 'Paulínia Centro', 42, 72, 0),
      mkVisita('sim-cons-v-a3', 3, 'ex-cli-campinas', 'EX-CP01', 'Campinas (2 km base)', 8, 4.8, 0),
    ],
  };

  const rotaB = {
    id: 'sim-cons-rota-b',
    data: '2026-03-21',
    tecnico_nome: 'Técnico Exemplo',
    carro_placa: 'CONS-1001',
    status: 'finalizada',
    km_total: 95,
    custo_total: 114,
    is_simulacao: true,
    visitas: [
      mkVisita('sim-cons-v-b1', 1, 'ex-cli-campinas', 'EX-CP01', 'Campinas (2 km base)', 12, 4.8, 0),
      mkVisita('sim-cons-v-b2', 2, 'ex-cli-americana', 'EX-AM01', 'Americana Sul', 55, 90, 0),
    ],
  };

  const rotaC = {
    id: 'sim-cons-rota-c',
    data: '2026-03-24',
    tecnico_nome: 'Outro Técnico',
    carro_placa: 'CONS-2002',
    status: 'finalizada',
    km_total: 25,
    custo_total: 30,
    is_simulacao: true,
    visitas: [mkVisita('sim-cons-v-c1', 1, 'ex-cli-campinas', 'EX-CP01', 'Campinas (2 km base)', 10, 4.8, 0)],
  };

  const manutencoes = [
    {
      id: 'sim-man-a-rp',
      rota_id: 'sim-cons-rota-a',
      cliente_id: 'ex-cli-ribeirao',
      custo_pecas: 420.5,
      pecas: [
        { nome: 'Elemento filtro', quantidade: 2, custo_unitario: 150, valor_total: 300 },
        { nome: 'Óleo sintético', quantidade: 1, custo_unitario: 120.5, valor_total: 120.5 },
      ],
    },
    {
      id: 'sim-man-a-pl',
      rota_id: 'sim-cons-rota-a',
      cliente_id: 'ex-cli-paulinia',
      custo_pecas: 88,
      pecas: [{ nome: 'Junta', quantidade: 4, custo_unitario: 22, valor_total: 88 }],
    },
    {
      id: 'sim-man-a-cp',
      rota_id: 'sim-cons-rota-a',
      cliente_id: 'ex-cli-campinas',
      custo_pecas: 0,
      pecas: [],
    },
    {
      id: 'sim-man-b-cp',
      rota_id: 'sim-cons-rota-b',
      cliente_id: 'ex-cli-campinas',
      custo_pecas: 65,
      pecas: [{ nome: 'Sensor pressão', quantidade: 1, custo_unitario: 65, valor_total: 65 }],
    },
    {
      id: 'sim-man-b-am',
      rota_id: 'sim-cons-rota-b',
      cliente_id: 'ex-cli-americana',
      custo_pecas: 310,
      pecas: [{ nome: 'Kit manutenção', quantidade: 1, custo_unitario: 310, valor_total: 310 }],
    },
    {
      id: 'sim-man-c-cp',
      rota_id: 'sim-cons-rota-c',
      cliente_id: 'ex-cli-campinas',
      custo_pecas: 45.75,
      pecas: [{ nome: 'Filtro ar', quantidade: 3, custo_unitario: 15.25, valor_total: 45.75 }],
    },
  ];

  return { rotas: [rotaA, rotaB, rotaC], manutencoes };
};
